<template>
  <div
    v-wave
    class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 md:rounded-md md:border md:my-2"
    :class="voucher.used ? 'opacity-60' : ''"
    @click="$emit('click')"
  >
    <div class="w-11 h-11 rounded-full flex items-center justify-center bg-gradient-to-br from-[#ddd8fc] to-[#a691f4] flex-shrink-0">
      <i class="pi pi-ticket text-base text-primary" />
    </div>

    <div class="flex-1 min-w-0">
      <div class="flex items-baseline justify-between gap-2">
        <div class="text-[15px] font-semibold text-gray-900 truncate">
          {{ title }}
        </div>
        <span
          class="text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
          :class="statusChip.classes"
        >{{ statusChip.label }}</span>
      </div>

      <div class="flex items-center gap-1.5 mt-1 text-[13px] text-gray-600">
        <span>הופק {{ formattedFrom }}</span>
      </div>

      <div class="flex items-center gap-1.5 mt-2 text-[12px] text-gray-500">
        <i class="pi pi-clock text-[11px]" />
        <span>בתוקף עד {{ formattedUntil }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { DateTime } from 'luxon';
import { IVoucher, VoucherTypes } from '@torim-app/types';
import { useAdminStore } from '@app/stores';

const adminStore = useAdminStore();

const props = defineProps({
  voucher: {
    type: Object as PropType<IVoucher>,
    required: true
  }
});

defineEmits<{ (e: 'click'): void }>();

const formattedFrom = computed(() =>
  DateTime.fromISO(props.voucher.validFromDate as string).toFormat('dd.MM.yyyy')
);

const formattedUntil = computed(() =>
  DateTime.fromISO(props.voucher.validUntilDate as string).toFormat('dd.MM.yyyy')
);

const isExpired = computed(() =>
  !props.voucher.used && DateTime.fromISO(props.voucher.validUntilDate as string) < DateTime.now()
);

const statusChip = computed(() => {
  if (props.voucher.used) return { label: 'מומש', classes: 'bg-gray-100 text-gray-600' };
  if (isExpired.value) return { label: 'פג תוקף', classes: 'bg-red-100 text-red-700' };
  return { label: 'בתוקף', classes: 'bg-green-100 text-green-700' };
});

const title = computed(() => {
  const voucher = props.voucher;
  if (voucher.voucherType === VoucherTypes.AMOUNT)
    return `שובר הנחה על סך ${voucher.amount} ש"ח`;
  if (!adminStore.businessSiteData.services || !voucher.serviceId) return 'שובר';
  const services = adminStore.businessSiteData.services.filter(x => voucher.serviceId!.includes(x.id));
  return `שובר ${voucher.serviceDiscount}% הנחה ל${services.map(x => x.name).join(', ')}`;
});
</script>
